import { useCallback, useSyncExternalStore } from 'react';
import { useFeatureVersion } from '@hooks/useFeatureVersion';
import { AGENT_CHAT_FEATURE_KEY } from '@utils/featureRollout/constants';
import {
  clearRevertFeedback,
  isRevertFeedbackRequested,
  subscribeRevertFeedback,
} from '@utils/featureRollout/revertFeedback';
import { AGENT_CHAT_REVERT_FEEDBACK } from '@utils/featureRollout/strings';
import { RevertFeedbackPrompt, type FeedbackOption } from '@components/v4/featureRollout/RevertFeedbackPrompt';
import useTracking, { TRACK_EVENT_NAMES } from '@utils/hooks/useTracking';

/**
 * PaulinaRolloutOverlays — prompt de feedback al DESACTIVAR Paulina (feature agent-chat).
 *
 * Gemelo recortado de BlePrintingRolloutOverlays: SOLO el prompt de feedback al quitar (revert),
 * pedido vía la señal de módulo `revertFeedback` (keyed por feature) y renderizado AQUÍ, a nivel
 * HOST (junto al AgentChatWidget en Layout) — la superficie que lo pide (el control "Desactivar
 * Paulina" del header) se desmonta con el revert optimista (Paulina deja de montarse) y un prompt
 * montado dentro moriría con ella.
 *
 * Detalle heredado de menú/BLE (Seer): onSubmit SOLO envía la telemetría y NO limpia el signal —
 * limpiarlo síncrono desmontaría el prompt en el mismo batch que su setSent(true) interno,
 * tragándose la pantalla de agradecimiento. El cierre se difiere al onSkip (X / backdrop).
 *
 * Los chips consumen el catálogo per-experimento (feedback_options) con fallback a las copys de
 * agent-chat de strings.ts; 'Otro' SIEMPRE lo agrega el FE al final.
 *
 * Telemetría: al enviar → PaulinaFeedbackSent `{ tipo:'revert', motivo, util:false }` (pipeline
 * canónico useTracking, igual que el resto de Paulina). El behavior 'revert_feedback' (telemetría
 * del framework) lo emite submitRevertFeedback en paralelo.
 *
 * NOTA (alcance): la encuesta de permanencia / usage_feedback (👍/👎 tras N usos) queda FUERA de
 * esta primera entrega — para Paulina el revert manda al backend la señal accionable; el
 * usage_feedback se puede sumar después reusando este mismo molde con submitUsageFeedback.
 */

const OTHER: FeedbackOption = { value: 'other', label: 'Otro' };

const overlayStyle = {
  position: 'fixed' as const,
  inset: 0,
  background: 'rgba(17,26,34,.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  zIndex: 1000,
  fontFamily: 'inherit',
};

export function PaulinaRolloutOverlays() {
  const { trackEvent } = useTracking();
  const { submitRevertFeedback, feedbackOptions } = useFeatureVersion(AGENT_CHAT_FEATURE_KEY);

  const subscribeFeedback = useCallback((cb: () => void) => subscribeRevertFeedback(AGENT_CHAT_FEATURE_KEY, cb), []);
  const getFeedbackRequested = useCallback(() => isRevertFeedbackRequested(AGENT_CHAT_FEATURE_KEY), []);
  const feedbackRequested = useSyncExternalStore(subscribeFeedback, getFeedbackRequested, getFeedbackRequested);

  if (!feedbackRequested) return null;

  // Catálogo per-experimento con fallback a las copys de agent-chat; 'Otro' al final, FE-side.
  const revertOptions = feedbackOptions?.revert
    ? [...feedbackOptions.revert, OTHER]
    : AGENT_CHAT_REVERT_FEEDBACK.options;

  return (
    <div
      style={overlayStyle}
      onClick={(e) => {
        if (e.target === e.currentTarget) clearRevertFeedback(AGENT_CHAT_FEATURE_KEY);
      }}
    >
      <div style={{ width: '100%', maxWidth: 420 }}>
        <RevertFeedbackPrompt
          title={AGENT_CHAT_REVERT_FEEDBACK.title}
          subtitle={AGENT_CHAT_REVERT_FEEDBACK.subtitle}
          options={revertOptions}
          textPlaceholder={AGENT_CHAT_REVERT_FEEDBACK.textPlaceholder}
          onSubmit={(reason, text) => {
            submitRevertFeedback(reason, text, 'revert_feedback');
            trackEvent(TRACK_EVENT_NAMES.PaulinaFeedbackSent, { tipo: 'revert', motivo: reason, util: false });
          }}
          onSkip={() => clearRevertFeedback(AGENT_CHAT_FEATURE_KEY)}
        />
      </div>
    </div>
  );
}
